import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Avatar,
} from '@mui/material';
import {
  Add,
  Search,
  LocalOffer,
  Schedule,
  AttachMoney,
  Refresh,
  Download,
} from '@mui/icons-material';
import DataTable, { Column } from '../components/common/DataTable';
import PackageForm from '../components/packages/PackageForm';
import DeletePackageDialog from '../components/packages/DeletePackageDialog';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { packageService, ServicePackageResponse, CreatePackageData, PackageServiceItem } from '../services/packageService';
import '../styles/minimal-padding.css';

// Enhanced Metric Card Component with forwardRef
interface MetricCardProps {
  title: string;
  value: string | number | React.ReactNode;
  bgGradient: string;
  icon: React.ReactNode;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  bgGradient,
  icon,
}, ref) => (
  <Card
    ref={ref}
    className="metric-card"
    sx={{
      background: bgGradient,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::before': {
        transform: 'translateX(100%)',
      },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#FFFFFF !important',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              mb: 1,
              display: 'block',
              opacity: 0.9,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF !important',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              lineHeight: 1.2,
              mb: 1,
            }}
            style={{ color: '#FFFFFF !important' }}
          >
            {value}
          </Typography>
        </Box>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            width: 70, 
            height: 70,
            color: 'white',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ fontSize: { xs: 30, sm: 35 } }}>
            {icon}
          </Box>
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

MetricCard.displayName = 'MetricCard';

interface ServicePackage extends ServicePackageResponse {}

const Packages: React.FC = () => {
  const dispatch = useAppDispatch();
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<ServicePackage[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedPackage, setSelectedPackage] = useState<ServicePackage | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load packages from API
  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await packageService.getPackages({
        search: searchTerm || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      });
      setPackages(response.packages);
      setFilteredPackages(response.packages);
      setTotalCount(response.total || 0);
    } catch (err) {
      console.error('Failed to load packages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load packages');
      setPackages([]);
      setFilteredPackages([]);
      setTotalCount(0);
      dispatch(addNotification({ type: 'error', message: 'Failed to load packages' }));
    } finally {
      setLoading(false);
    }
  };

  // Load packages on component mount and when filters change
  useEffect(() => {
    loadPackages();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  const handleViewPackage = (pkg: ServicePackage) => {
    setSelectedPackage(pkg);
    setViewDialogOpen(true);
  };

  const handleEditPackage = (pkg: ServicePackage) => {
    setSelectedPackage(pkg);
    setFormMode('edit');
    setFormDialogOpen(true);
  };

  const handleDeletePackage = (pkg: ServicePackage) => {
    setSelectedPackage(pkg);
    setDeleteDialogOpen(true);
  };

  const handleCreatePackage = () => {
    setSelectedPackage(null);
    setFormMode('create');
    setFormDialogOpen(true);
  };

  const handleFormSubmit = async (data: CreatePackageData) => {
    try {
      setLoading(true);
      if (formMode === 'create') {
        await packageService.createPackage(data);
        dispatch(addNotification({
          type: 'success',
          message: 'Package created successfully',
        }));
      } else if (selectedPackage) {
        await packageService.updatePackage({ ...data, id: selectedPackage.id });
        dispatch(addNotification({
          type: 'success',
          message: 'Package updated successfully',
        }));
      }
      loadPackages();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `Failed to ${formMode} package: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPackage) return;
    
    try {
      setLoading(true);
      await packageService.deletePackage(selectedPackage.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Package deleted successfully',
      }));
      setDeleteDialogOpen(false);
      loadPackages();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: `Failed to delete package: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  const getTotalDuration = (packageServices: ServicePackage['packageServices']) => {
    return packageServices.reduce((total, ps) => 
      total + (ps.service.duration * ps.quantity), 0
    );
  };

  const columns: Column[] = [
    {
      id: 'name',
      label: 'Package Name',
      format: (_value: string, pkg: ServicePackage) => (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {pkg.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {pkg.description}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'services',
      label: 'Services',
      format: (_value: any, pkg: ServicePackage) => (
        <Box>
          <Typography variant="body2">
            {pkg.packageServices.length} service{pkg.packageServices.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDuration(getTotalDuration(pkg.packageServices))} total
          </Typography>
        </Box>
      ),
    },
    {
      id: 'pricing',
      label: 'Pricing',
      format: (_value: any, pkg: ServicePackage) => (
        <Box>
          <Typography variant="body2" sx={{ textDecoration: 'line-through' }} color="text.secondary">
            {formatCurrency(pkg.totalPrice)}
          </Typography>
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            {formatCurrency(pkg.discountedPrice)}
          </Typography>
          <Chip
            label={`${pkg.discountPercentage}% OFF`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      id: 'validityDays',
      label: 'Validity',
      format: (value: number) => (
        <Typography variant="body2">
          {value} days
        </Typography>
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      format: (value: boolean) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  color: '#5C2A30',
                  mb: 1,
                }}
              >
                Service Packages
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(92, 42, 48, 0.85)',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                }}
              >
                Manage service packages and pricing bundles
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Report" arrow>
                <IconButton 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.4)',
                    color: '#5C2A30',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(92, 42, 48, 0.18)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreatePackage}
                disabled={loading}
                sx={{
                  bgcolor: 'white',
                  color: '#2D3748',
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: '#1A202C',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
                    border: '2px solid white',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Create Package
              </Button>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Enhanced Error Alert */}
      {error && (
        <Fade in timeout={300}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              bgcolor: 'rgba(237, 137, 54, 0.1)',
              border: '1px solid rgba(237, 137, 54, 0.2)',
              '& .MuiAlert-icon': {
                color: '#ED8936',
              },
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
          </Alert>
        </Fade>
      )}

      {/* Enhanced Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <MetricCard
              title="Total Packages"
              value={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : packages.length}
              bgGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              icon={<LocalOffer />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <MetricCard
              title="Active Packages"
              value={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : packages.filter(p => p.isActive).length}
              bgGradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              icon={<LocalOffer />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <MetricCard
              title="Avg. Discount"
              value={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `${packages.length > 0 ? Math.round(packages.reduce((avg, p) => avg + p.discountPercentage, 0) / packages.length) : 0}%`}
              bgGradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              icon={<AttachMoney />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <MetricCard
              title="Avg. Validity"
              value={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : `${packages.length > 0 ? Math.round(packages.reduce((avg, p) => avg + p.validityDays, 0) / packages.length) : 0} days`}
              bgGradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
              icon={<Schedule />}
            />
          </Zoom>
        </Grid>
      </Grid>

      {/* Enhanced Filters */}
      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Card 
          sx={{ 
            mb: 3,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#E8B4B8' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      '&:hover fieldset': {
                        borderColor: '#E8B4B8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#E8B4B8',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      borderRadius: 3,
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E8B4B8',
                      },
                    }}
                  >
                    <MenuItem value="all">All Packages</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="inactive">Inactive Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Packages Table */}
      <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
        <Card 
          sx={{ 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
            },
          }}
        >
          <DataTable
            columns={columns}
            rows={error ? filteredPackages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : filteredPackages}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount || 0}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            onView={handleViewPackage}
            onEdit={handleEditPackage}
            onDelete={handleDeletePackage}
            loading={loading}
          />
        </Card>
      </Fade>

      {/* Package Form Dialog */}
      <PackageForm
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        mode={formMode}
        initialData={selectedPackage ? {
          name: selectedPackage.name,
          description: selectedPackage.description,
          services: selectedPackage.packageServices.map(ps => ({
            serviceId: ps.service.id,
            quantity: ps.quantity,
          })) as PackageServiceItem[],
          discountPercentage: selectedPackage.discountPercentage,
          validityDays: selectedPackage.validityDays,
          isActive: selectedPackage.isActive,
        } : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <DeletePackageDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        package={selectedPackage}
        loading={loading}
      />

      {/* Package Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Package Details</Typography>
            <Chip
              label={selectedPackage?.isActive ? 'Active' : 'Inactive'}
              color={selectedPackage?.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Box>
              <Typography variant="h5" gutterBottom>
                {selectedPackage.name}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {selectedPackage.description}
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Original Price
                  </Typography>
                  <Typography variant="h6" sx={{ textDecoration: 'line-through' }}>
                    {formatCurrency(selectedPackage.totalPrice)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Package Price
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(selectedPackage.discountedPrice)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    You Save
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(selectedPackage.totalPrice - selectedPackage.discountedPrice)} ({selectedPackage.discountPercentage}%)
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Validity
                  </Typography>
                  <Typography variant="h6">
                    {selectedPackage.validityDays} days
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Included Services
              </Typography>
              <List>
                {selectedPackage.packageServices.map((ps) => (
                  <ListItem key={ps.id} divider>
                    <ListItemText
                      primary={ps.service.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {ps.service.category} • {formatDuration(ps.service.duration)}
                          </Typography>
                          {ps.quantity > 1 && (
                            <Typography variant="body2" color="primary">
                              Quantity: {ps.quantity}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="subtitle2">
                        {formatCurrency(ps.service.price)}
                        {ps.quantity > 1 && (
                          <Typography variant="caption" display="block">
                            × {ps.quantity}
                          </Typography>
                        )}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Package Summary
                </Typography>
                <Typography variant="body2">
                  Total Duration: {formatDuration(getTotalDuration(selectedPackage.packageServices))}
                </Typography>
                <Typography variant="body2">
                  Total Services: {selectedPackage.packageServices.reduce((total, ps) => total + ps.quantity, 0)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => handleEditPackage(selectedPackage!)}>
            Edit Package
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Packages;